import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Package,
  Wrench,
  Boxes,
  Truck,
  Calendar,
  Ticket,
  ClipboardCheck,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import { Dialog, DialogContent } from "@/shared/ui";
import { StatusChip } from '@/shared/components';
import { useDebounce } from "@/shared/hooks";
import { graphqlFetch } from "@/shared/graphql/graphqlFetch";
import {
  GLOBAL_SEARCH,
  type GlobalSearchResult,
} from "@/shared/graphql/queries";
import { PATHS } from "@/app/router";
import { getStatusColor } from "@/shared/utils/statusChipColors";

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORY_ORDER: (keyof GlobalSearchResult)[] = [
  "assets",
  "maintenance",
  "inspection",
  "parts",
  "suppliers",
  "events",
  "tickets",
];

const CATEGORY_ICONS: Record<keyof GlobalSearchResult, React.ComponentType<{ className?: string }>> = {
  assets: Package,
  maintenance: Wrench,
  inspection: ClipboardCheck,
  parts: Boxes,
  suppliers: Truck,
  events: Calendar,
  tickets: Ticket,
};

const CATEGORY_I18N_KEYS: Record<keyof GlobalSearchResult, string> = {
  assets: "search.categories.assets",
  maintenance: "search.categories.maintenance",
  inspection: "search.categories.inspection",
  parts: "search.categories.parts",
  suppliers: "search.categories.suppliers",
  events: "search.categories.events",
  tickets: "search.categories.tickets",
};

function getPath(category: keyof GlobalSearchResult, id: string): string {
  switch (category) {
    case "assets":
      return `/${PATHS.ASSETS}/${id}`;
    case "maintenance":
      return `/${PATHS.MAINTENANCE}/${id}`;
    case "inspection":
      return `/${PATHS.INSPECTIONS}/${id}`;
    case "parts":
      return `/${PATHS.PARTS}/${id}`;
    case "suppliers":
      return `/${PATHS.SUPPLIERS}/${id}`;
    case "events":
      return `/${PATHS.EVENTS}/${id}`;
    case "tickets":
      return `/${PATHS.TICKETS}/${id}`;
    default:
      return "/";
  }
}

function getSubtitle(category: keyof GlobalSearchResult, item: Record<string, unknown>): string | null {
  switch (category) {
    case "assets":
      return (item.assetNumber as string) ?? null;
    case "maintenance":
    case "inspection":
    case "tickets":
      return null;
    case "parts":
      return (item.number as string) ?? null;
    case "suppliers":
      return (item.city as string) ?? null;
    case "events":
      return item.startsAt
        ? new Date(item.startsAt as string).toLocaleDateString()
        : null;
    default:
      return null;
  }
}

function getTitle(category: keyof GlobalSearchResult, item: Record<string, unknown>): string {
  switch (category) {
    case "assets":
    case "parts":
    case "suppliers":
      return (item.name as string) ?? "";
    case "maintenance":
    case "inspection":
    case "tickets":
      return (item.title as string) ?? "";
    case "events":
      return (item.name as string) ?? "";
    default:
      return "";
  }
}

function translateMaintenanceOrInspectionStatus(
  t: (key: string) => string,
  workflowStatus: string,
  isInspection: boolean,
): string {
  const ns = isInspection ? "inspection" : "maintenance";
  const key = `pages:${ns}.form.workflowStatus.${workflowStatus.toLowerCase()}`;
  return t(key) || workflowStatus;
}

/** Normalize backend orderStatus to translation key (none | ordered | delivered). */
function normalizePartOrderStatus(orderStatus: string): "none" | "ordered" | "delivered" {
  const s = orderStatus.trim().toUpperCase();
  if (s === "ORDERED") return "ordered";
  if (s === "DELIVERED") return "delivered";
  return "none";
}

function translatePartOrderStatus(t: (key: string) => string, orderStatus: string): string {
  const key = normalizePartOrderStatus(orderStatus);
  return t(`pages:parts.details.orderStatuses.${key}`);
}

function translateTicketStatus(t: (key: string) => string, status: string): string {
  const key = `pages:tickets.status.${status}`;
  return t(key) || status;
}

function SearchItemBadges({
  category,
  item,
  t,
}: {
  category: keyof GlobalSearchResult;
  item: Record<string, unknown>;
  t: (key: string) => string;
}) {
  if (category === "assets") {
    const labels = (item.labels as string[] | undefined) ?? [];
    const status = item.status as string | undefined;
    if (labels.length === 0 && !status) return null;
    return (
      <span className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
        {labels.map((label) => (
          <StatusChip key={label} text={label} color="#64748b" size="sm" />
        ))}
        {status && (
          <StatusChip
            text={status}
            color={getStatusColor(status.toLowerCase())}
            size="sm"
          />
        )}
      </span>
    );
  }
  if (category === "parts") {
    const currentStock = item.currentStock as number | undefined | null;
    const orderStatus = item.orderStatus as string | undefined | null;
    if (currentStock == null && !orderStatus) return null;
    const statusKey = orderStatus ? normalizePartOrderStatus(orderStatus) : null;
    return (
      <span className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
        {currentStock != null && (
          <StatusChip
            text={String(currentStock)}
            color="#64748b"
            size="sm"
          />
        )}
        {orderStatus && statusKey && (
          <StatusChip
            text={translatePartOrderStatus(t, orderStatus)}
            color={getStatusColor(statusKey)}
            size="sm"
          />
        )}
      </span>
    );
  }
  if (category === "maintenance") {
    const workflowStatus = item.workflowStatus as string | undefined;
    if (!workflowStatus) return null;
    return (
      <StatusChip
        text={translateMaintenanceOrInspectionStatus(t, workflowStatus, false)}
        color={getStatusColor(workflowStatus.toLowerCase())}
        size="sm"
      />
    );
  }
  if (category === "inspection") {
    const workflowStatus = item.workflowStatus as string | undefined;
    if (!workflowStatus) return null;
    return (
      <StatusChip
        text={translateMaintenanceOrInspectionStatus(t, workflowStatus, true)}
        color={getStatusColor(workflowStatus.toLowerCase())}
        size="sm"
      />
    );
  }
  if (category === "events") {
    const eventType = item.eventType as string | undefined;
    if (!eventType) return null;
    return (
      <StatusChip text={eventType} color="#64748b" size="sm" />
    );
  }
  if (category === "tickets") {
    const status = item.status as string | undefined;
    if (!status) return null;
    return (
      <StatusChip
        text={translateTicketStatus(t, status)}
        color={getStatusColor(status)}
        size="sm"
      />
    );
  }
  return null;
}

export const GlobalSearchDialog = ({ open, onOpenChange }: GlobalSearchDialogProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<GlobalSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults(null);
    }
  }, [open]);

  useEffect(() => {
    const runSearch = async () => {
      if (debouncedSearch?.length < 2) {
        setSearchResults(null);
        return;
      }

      setIsLoading(true);
      try {
        const data = await graphqlFetch<{ globalSearch: GlobalSearchResult }>(
          GLOBAL_SEARCH,
          { query: debouncedSearch },
        );
        setSearchResults(data.globalSearch);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults({
          assets: [],
          maintenance: [],
          parts: [],
          suppliers: [],
          events: [],
          tickets: [],
          inspection: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    runSearch();
  }, [debouncedSearch]);

  const handleSelect = (path: string) => {
    navigate(path);
    onOpenChange(false);
    setSearchQuery("");
  };

  const hasAnyResults =
    searchResults != null &&
    CATEGORY_ORDER.some((key) => (searchResults[key] ?? []).length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-3xl">
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder={t("search.placeholder")}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[500px] overflow-y-auto">
            {isLoading ? (
              <CommandEmpty>{t("search.loading")}</CommandEmpty>
            ) : debouncedSearch?.length < 2 ? (
              <CommandEmpty>{t("search.minChars")}</CommandEmpty>
            ) : searchResults && !hasAnyResults ? (
              <CommandEmpty>{t("search.noResults")}</CommandEmpty>
            ) : null}

            {searchResults &&
              CATEGORY_ORDER.map((category) => {
                const items = searchResults[category];
                if (!items?.length) return null;

                const Icon = CATEGORY_ICONS[category];
                const headingKey = CATEGORY_I18N_KEYS[category];

                return (
                  <CommandGroup
                    key={category}
                    heading={t(headingKey)}
                  >
                    {items.map((item) => {
                      const id = (item as { id: string }).id;
                      const path = getPath(category, id);
                      const title = getTitle(category, item as Record<string, unknown>);
                      const subtitle = getSubtitle(category, item as Record<string, unknown>);
                      const record = item as Record<string, unknown>;
                      const isParts = category === "parts";

                      return (
                        <CommandItem
                          key={id}
                          onSelect={() => handleSelect(path)}
                        >
                          <Icon className="mr-2 h-4 w-4 flex-shrink-0" />
                          <div className={`flex-1 min-w-0 flex items-center gap-2 ${isParts ? "flex-row" : "flex-wrap"}`}>
                            <div className="min-w-0 flex flex-col gap-0.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{title}</span>
                                {!isParts && <SearchItemBadges category={category} item={record} t={t} />}
                              </div>
                              {subtitle && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {subtitle}
                                </span>
                              )}
                            </div>
                            {isParts && <SearchItemBadges category={category} item={record} t={t} />}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                );
              })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
